import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Calendar as CalendarIcon, Phone, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchUpcomingCallbacks } from "../../services/crmServices/calendarService";

const TYPE_DOT = {
    callback: "#3B82F6",
    follow_up: "#8B5CF6",
    meeting: "#10B981",
    task: "#F59E0B"
};

function formatWhen(date) {
    const d = new Date(date);
    const now = new Date();
    const diffMin = Math.round((d - now) / 60000);
    if (diffMin < 0) return "now";
    if (diffMin < 60) return `in ${diffMin}m`;
    if (diffMin < 24 * 60) {
        const h = Math.floor(diffMin / 60);
        const m = diffMin % 60;
        return m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
    }
    return d.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function formatExactTime(date) {
    const d = new Date(date);
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export function UpcomingCallbacksWidget({ limit = 5 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await fetchUpcomingCallbacks(limit);
                if (mounted) setItems(res?.data || []);
            } catch {
                if (mounted) setItems([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        // Refresh every 60s in case events change
        const t = setInterval(load, 60 * 1000);
        return () => { mounted = false; clearInterval(t); };
    }, [limit]);

    return (
        <Card className="border-gray-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-blue-600" />
                    Upcoming Callbacks
                </CardTitle>
                <Link to="/calendar" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
                    View all <ChevronRight className="h-3 w-3" />
                </Link>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-xs text-gray-400 py-2">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-6">
                        <p className="text-sm text-gray-500">No callbacks scheduled</p>
                        <Link to="/calendar" className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block">
                            Schedule one →
                        </Link>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {items.map((ev) => {
                            const lead = ev.leadSnapshot || {};
                            const leadName = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
                            return (
                                <li key={ev._id} className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
                                    <span
                                        style={{ backgroundColor: TYPE_DOT[ev.type] || "#3B82F6" }}
                                        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {ev.title}
                                            </p>
                                            <span className="text-xs text-blue-600 font-medium whitespace-nowrap" title={formatExactTime(ev.startAt)}>
                                                {formatWhen(ev.startAt)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500">
                                            {leadName && <span className="truncate">{leadName}</span>}
                                            {lead.phone && (
                                                <a href={`tel:${lead.phone}`} className="flex items-center gap-0.5 text-green-600 hover:text-green-800">
                                                    <Phone className="h-3 w-3" />
                                                    {lead.phone}
                                                </a>
                                            )}
                                            <span className="flex items-center gap-0.5">
                                                <Clock className="h-3 w-3" />
                                                {formatExactTime(ev.startAt)}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}
